import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './HomePage.css';
import API_BASE_URL from '../config/api';

const relationshipOptions = [
  "Head of Family","Father", "Mother", "Son", "Daughter", "Brother", "Sister",
  "Grandfather", "Grandmother", "Grandson", "Granddaughter",
  "Uncle", "Aunt", "Nephew", "Niece", "Cousin",
  "Father-in-law", "Mother-in-law", "Son-in-law", "Daughter-in-law",
  "Brother-in-law", "Sister-in-law", "Husband", "Wife", "Guardian"
];

const FamilyForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname === '/edit-family';
  const existingFamily = location.state?.family;

  const [saving, setSaving] = useState(false);
  const [familyData, setFamilyData] = useState({
    familyName: '',
    contactAddress: '',
    landline: '',
    motherParish: '',
    prayerGroup: '',
    hofPhotoId: null,
    hofPhotoUrl: '',
    familyPhotoId: null,
    familyPhotoUrl: '',
    vehicleNumbers: [''],
    maps: ''
  });
  const [uploadStates, setUploadStates] = useState({
    hofPhoto: { loading: false, error: null },
    familyPhoto: { loading: false, error: null }
  });
  const [members, setMembers] = useState([{
    fullName: '',
    role: 'USER',
    relationshipToHof: '',
    email: '',
    phoneNumber: '',
    bloodGroup: '',
    dateOfBirth: '',
    anniversaryDate: ''
  }]);

  useEffect(() => {
    if (isEdit && existingFamily) {
      setFamilyData({
        familyName: existingFamily.familyName || '',
        contactAddress: existingFamily.contactAddress || '',
        landline: existingFamily.landline || '',
        motherParish: existingFamily.motherParish || '',
        prayerGroup: existingFamily.prayerGroup || '',
        hofPhotoId: existingFamily.hofPhotoId || null,
        hofPhotoUrl: existingFamily.hofPhotoUrl || '',
        familyPhotoId: existingFamily.familyPhotoId || null,
        familyPhotoUrl: existingFamily.familyPhotoUrl || '',
        vehicleNumbers: existingFamily.vehicleNumbers?.length ? existingFamily.vehicleNumbers : [''],
        maps: existingFamily.maps || ''
      });
      setMembers(existingFamily.members || [{
        fullName: '',
        role: 'USER',
        relationshipToHof: '',
        email: '',
        phoneNumber: '',
        bloodGroup: '',
        dateOfBirth: '',
        anniversaryDate: ''
      }]);
    }
  }, [isEdit, existingFamily]);

  const handleFamilyChange = (field, value) => {
    setFamilyData(prev => ({ ...prev, [field]: value }));
  };

  const handleVehicleChange = (index, value) => {
    const newVehicles = [...familyData.vehicleNumbers];
    newVehicles[index] = value;
    setFamilyData(prev => ({ ...prev, vehicleNumbers: newVehicles }));
  };

  const addVehicle = () => {
    setFamilyData(prev => ({ ...prev, vehicleNumbers: [...prev.vehicleNumbers, ''] }));
  };

  const removeVehicle = (index) => {
    const newVehicles = familyData.vehicleNumbers.filter((_, i) => i !== index);
    setFamilyData(prev => ({ ...prev, vehicleNumbers: newVehicles }));
  };

  const handleMemberChange = (index, field, value) => {
    const newMembers = [...members];
    newMembers[index][field] = value;
    setMembers(newMembers);
  };

  const addMember = () => {
    setMembers([...members, {
      fullName: '',
      role: 'USER',
      relationshipToHof: '',
      email: '',
      phoneNumber: '',
      bloodGroup: '',
      dateOfBirth: '',
      anniversaryDate: ''
    }]);
  };

  const removeMember = (index) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (file, photoType) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Set loading state
    setUploadStates(prev => ({
      ...prev,
      [photoType]: { loading: true, error: null }
    }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update both ID and URL fields
        setFamilyData(prev => ({
          ...prev,
          [`${photoType}Id`]: data.id,
          [`${photoType}Url`]: data.signedUrl
        }));
        // Clear loading state
        setUploadStates(prev => ({
          ...prev,
          [photoType]: { loading: false, error: null }
        }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      // Set error state
      setUploadStates(prev => ({
        ...prev,
        [photoType]: { loading: false, error: 'Upload failed. Please try again.' }
      }));
    }
  };

  const isUploading = uploadStates.hofPhoto.loading || uploadStates.familyPhoto.loading;

  const handleSave = async () => {
    if (isUploading) return; // Block submission during uploads
    
    setSaving(true);
    
    try {
      if (isEdit) {
        // Update existing family
        const familyResponse = await fetch(`${API_BASE_URL}/api/families/${existingFamily.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            ...familyData,
            vehicleNumbers: familyData.vehicleNumbers.filter(v => v.trim())
          })
        });
        
        if (familyResponse.ok) {
          const membersResponse = await fetch(`${API_BASE_URL}/api/families/${existingFamily.id}/members`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify(members.filter(m => m.fullName.trim()))
          });
          
          if (membersResponse.ok) {
            navigate('/');
          }
        }
      } else {
        // Create new family
        const familyResponse = await fetch(`${API_BASE_URL}/api/families`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            ...familyData,
            vehicleNumbers: familyData.vehicleNumbers.filter(v => v.trim())
          })
        });
        
        if (familyResponse.ok) {
          const familyResult = await familyResponse.json();
          
          const membersResponse = await fetch(`${API_BASE_URL}/api/families/${familyResult.id}/members`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify(members.filter(m => m.fullName.trim()))
          });
          
          if (membersResponse.ok) {
            navigate('/');
          }
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const prayerGroups = ['AECS', 'Marathahalli', 'Munnekolala', 'Murugeshpalya', 'Ramesh Nagar', 'Sarjapur', 'Whitefield'];

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="header-content">
          <div className="logo">
            <span className="cross-icon">✝</span>
            <h1>STMTC Admin Portal</h1>
          </div>
          <button onClick={() => navigate('/')} className="logout-btn">
            Back to Home
          </button>
        </div>
      </header>
      
      <main className="home-main">
        <div className="add-family-form">
          <h3>{isEdit ? 'Edit Family' : 'Add New Family'}</h3>
          <div className="form-section">
            <div className="form-row">
              <label className="form-label">Family Name *</label>
              <input
                type="text"
                className="form-input"
                value={familyData.familyName}
                onChange={(e) => handleFamilyChange('familyName', e.target.value)}
                required
              />
            </div>
            <div className="form-row">
              <label className="form-label">Contact Address *</label>
              <textarea
                className="form-input"
                value={familyData.contactAddress}
                onChange={(e) => handleFamilyChange('contactAddress', e.target.value)}
                required
              />
            </div>
            <div className="form-row">
              <label className="form-label">Landline</label>
              <input
                type="text"
                className="form-input"
                value={familyData.landline}
                onChange={(e) => handleFamilyChange('landline', e.target.value)}
              />
            </div>
            <div className="form-row">
              <label className="form-label">Mother Parish</label>
              <input
                type="text"
                className="form-input"
                value={familyData.motherParish}
                onChange={(e) => handleFamilyChange('motherParish', e.target.value)}
              />
            </div>
            <div className="form-row">
              <label className="form-label">Prayer Group</label>
              <select
                className="form-input"
                value={familyData.prayerGroup}
                onChange={(e) => handleFamilyChange('prayerGroup', e.target.value)}
              >
                <option value="">Select Prayer Group</option>
                {prayerGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label className="form-label">HOF Photo</label>
              <div className="photo-upload-container">
                <input
                  type="file"
                  className="form-input"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'hofPhoto')}
                  disabled={uploadStates.hofPhoto.loading}
                />
                {uploadStates.hofPhoto.loading && <span className="upload-spinner">⏳ Uploading...</span>}
                {uploadStates.hofPhoto.error && <span className="upload-error">{uploadStates.hofPhoto.error}</span>}
                {familyData.hofPhotoUrl && (
                  <img src={familyData.hofPhotoUrl} alt="HOF Preview" className="photo-preview" />
                )}
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Family Photo</label>
              <div className="photo-upload-container">
                <input
                  type="file"
                  className="form-input"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'familyPhoto')}
                  disabled={uploadStates.familyPhoto.loading}
                />
                {uploadStates.familyPhoto.loading && <span className="upload-spinner">⏳ Uploading...</span>}
                {uploadStates.familyPhoto.error && <span className="upload-error">{uploadStates.familyPhoto.error}</span>}
                {familyData.familyPhotoUrl && (
                  <img src={familyData.familyPhotoUrl} alt="Family Preview" className="photo-preview" />
                )}
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Vehicle Numbers</label>
              <div className="vehicles-container">
                {familyData.vehicleNumbers.map((vehicle, index) => (
                  <div key={index} className="vehicle-input">
                    <input
                      type="text"
                      className="vehicle-field"
                      placeholder="Vehicle Number"
                      value={vehicle}
                      onChange={(e) => handleVehicleChange(index, e.target.value)}
                    />
                    {familyData.vehicleNumbers.length > 1 && (
                      <button type="button" className="remove-btn" onClick={() => removeVehicle(index)}>➖</button>
                    )}
                  </div>
                ))}
                <button type="button" className="add-btn" onClick={addVehicle}>➕ Add Vehicle</button>
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Maps</label>
              <input
                type="text"
                className="form-input"
                value={familyData.maps}
                onChange={(e) => handleFamilyChange('maps', e.target.value)}
                placeholder="Maps"
              />
            </div>
          </div>

          <div className="members-section">
            <h3>Members</h3>
            {members.map((member, index) => (
              <div key={index} className="member-form">
                <div className="member-header">
                  <h4>Member {index + 1}</h4>
                  {members.length > 1 && (
                    <button type="button" className="remove-btn" onClick={() => removeMember(index)}>➖</button>
                  )}
                </div>
                <div className="form-section">
                  <div className="form-row">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={member.fullName}
                      onChange={(e) => handleMemberChange(index, 'fullName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Role</label>
                    <select
                      className="form-input"
                      value={member.role}
                      onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                    >
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </select>
                  </div>
                  <div className="form-row">
                    <label className="form-label">Relationship to HOF *</label>
                    <select
                      className="form-input"
                      value={member.relationshipToHof}
                      onChange={(e) => handleMemberChange(index, 'relationshipToHof', e.target.value)}
                      required
                    >
                      <option value="">Select Relationship</option>
                      {relationshipOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={member.email}
                      onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={member.phoneNumber}
                      onChange={(e) => handleMemberChange(index, 'phoneNumber', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Blood Group</label>
                    <input
                      type="text"
                      className="form-input"
                      value={member.bloodGroup}
                      onChange={(e) => handleMemberChange(index, 'bloodGroup', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="form-input"
                      value={member.dateOfBirth}
                      onChange={(e) => handleMemberChange(index, 'dateOfBirth', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Anniversary Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={member.anniversaryDate}
                      onChange={(e) => handleMemberChange(index, 'anniversaryDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="add-btn" onClick={addMember}>➕ Add Member</button>
          </div>

          <div className="save-section">
            <button 
              className="save-btn"
              onClick={handleSave}
              disabled={saving || isUploading}
            >
              {saving ? 'Saving...' : isUploading ? 'Uploading images...' : (isEdit ? 'Update Family' : 'Save Family')}
            </button>
            <button 
              className="logout-btn"
              onClick={() => navigate('/')}
              style={{ marginLeft: '1rem' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FamilyForm;
